import React from 'react';
import { InlineSpinner } from '../../common/loading';

import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Backdrop,
  Fade,
alpha,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { SEARCE_COLORS } from '../../../theme';
import { RequestType, SubmissionError } from './types';

interface SubmissionStatusModalProps {
  loading: boolean;
  fileUploading: boolean;
  slideCreating?: boolean;
  submissionSuccess: boolean;
  successMessage: string;
  submissionError: SubmissionError;
  selectedRequestType: RequestType;
  onRetryUpload: () => void;
  onClearError: () => void;
  onClearFile: () => void;
}

export const SubmissionStatusModal: React.FC<SubmissionStatusModalProps> = ({
  loading,
  fileUploading,
  slideCreating = false,
  submissionSuccess,
  successMessage,
  submissionError,
  selectedRequestType,
  onRetryUpload,
  onClearError,
  onClearFile,
}) => {
  const navigate = useNavigate();
  const isOpen = loading || submissionSuccess || submissionError.stage !== null;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: 1300,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
        }}
        open={isOpen}
      />
      
      {/* Floating Modal Box */}
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1400,
          backgroundColor: SEARCE_COLORS.white,
          borderRadius: 4,
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2), 0 12px 24px rgba(0, 0, 0, 0.1)',
          p: 5,
          minWidth: 380,
          maxWidth: 440,
          textAlign: 'center',
          animation: 'modalSlideIn 0.3s ease-out',
          '@keyframes modalSlideIn': {
            '0%': { opacity: 0, transform: 'translate(-50%, -48%) scale(0.96)' },
            '100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
          },
        }}
      >
        {/* Loading State */}
        {loading && !submissionSuccess && submissionError.stage === null && (
          <Fade in={loading}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
              {/* Animated Loader */}
              <Box
                sx={{
                  position: 'relative',
                  width: 72,
                  height: 72,
                }}
              >
                <InlineSpinner size={56} thickness={3} />
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {fileUploading ? (
                    <Box
                      component="span"
                      sx={{
                        fontSize: 20,
                        animation: 'bounce 1s ease-in-out infinite',
                        '@keyframes bounce': {
                          '0%, 100%': { transform: 'translateY(0)' },
                          '50%': { transform: 'translateY(-4px)' },
                        },
                      }}
                    >
                      📄
                    </Box>
                  ) : slideCreating ? (
                    <Box
                      component="span"
                      sx={{
                        fontSize: 20,
                        animation: 'pulse 1.5s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { transform: 'scale(1)' },
                          '50%': { transform: 'scale(1.1)' },
                        },
                      }}
                    >
                      📊
                    </Box>
                  ) : (
                    <Box
                      component="span"
                      sx={{
                        fontSize: 20,
                        animation: 'pulse 1.5s ease-in-out infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { transform: 'scale(1)' },
                          '50%': { transform: 'scale(1.1)' },
                        },
                      }}
                    >
                      🚀
                    </Box>
                  )}
                </Box>
              </Box>
              
              {/* Title */}
              <Typography 
                variant="h5" 
                sx={{ 
                  color: SEARCE_COLORS.textPrimary,
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                }}
              >
                {fileUploading 
                  ? 'Uploading File' 
                  : slideCreating 
                    ? 'Submitting for Review' 
                    : 'Submitting Request'}
              </Typography>
              
              {/* Subtitle */}
              <Typography 
                variant="body1" 
                sx={{ 
                  color: SEARCE_COLORS.textSecondary,
                  maxWidth: 300,
                  lineHeight: 1.6,
                }}
              >
                {fileUploading 
                  ? 'Please wait while we securely upload your files...' 
                  : slideCreating
                    ? 'Please wait while we submit your solution story for review...'
                    : selectedRequestType === 'solution-story'
                      ? 'Please wait while we submit your solution story for review...'
                      : 'Please wait while we process your submission...'}
              </Typography>
              
              {/* Progress indicator */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.75,
                  mt: 1,
                }}
              >
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: SEARCE_COLORS.primary,
                      animation: `dotPulse 1.4s ease-in-out ${i * 0.2}s infinite`,
                      '@keyframes dotPulse': {
                        '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
                        '40%': { opacity: 1, transform: 'scale(1)' },
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Fade>
        )}
        
        {/* Success State */}
        {submissionSuccess && (
          <Fade in={submissionSuccess}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
              {/* Animated Success Icon */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${alpha(SEARCE_COLORS.success, 0.15)} 0%, ${alpha(SEARCE_COLORS.success, 0.05)} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'successPop 0.5s ease-out',
                  '@keyframes successPop': {
                    '0%': { transform: 'scale(0)', opacity: 0 },
                    '50%': { transform: 'scale(1.1)' },
                    '100%': { transform: 'scale(1)', opacity: 1 },
                  },
                }}
              >
                <CheckCircleOutlineIcon 
                  sx={{ 
                    fontSize: 44, 
                    color: SEARCE_COLORS.success,
                    animation: 'checkmark 0.4s ease-out 0.2s both',
                    '@keyframes checkmark': {
                      '0%': { strokeDashoffset: 100 },
                      '100%': { strokeDashoffset: 0 },
                    },
                  }} 
                />
              </Box>
              
              {/* Success Title */}
              <Typography 
                variant="h5" 
                sx={{ 
                  color: SEARCE_COLORS.success,
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                }}
              >
                Success! 🎉
              </Typography>
              
              {/* Success Message */}
              <Typography 
                variant="body1" 
                sx={{ 
                  color: SEARCE_COLORS.textPrimary,
                  textAlign: 'center',
                  maxWidth: 320,
                  lineHeight: 1.6,
                }}
              >
                {successMessage}
              </Typography>
              
              {/* Success Badge - Show email badge for Feedback requests */}
              {selectedRequestType === 'feedback' && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2.5,
                    py: 1,
                    backgroundColor: alpha(SEARCE_COLORS.success, 0.08),
                    borderRadius: 3,
                    border: `1px solid ${alpha(SEARCE_COLORS.success, 0.2)}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: SEARCE_COLORS.success,
                      animation: 'blink 1.5s ease-in-out infinite',
                      '@keyframes blink': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.4 },
                      },
                    }}
                  />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: SEARCE_COLORS.success,
                      fontWeight: 600,
                    }}
                  >
                    Email sent to customer
                  </Typography>
                </Box>
              )}
              
              {/* Success Badge - Show slide creation badge for Solution Story requests */}
              {selectedRequestType === 'solution-story' && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2.5,
                    py: 1,
                    backgroundColor: alpha(SEARCE_COLORS.primary, 0.08),
                    borderRadius: 3,
                    border: `1px solid ${alpha(SEARCE_COLORS.primary, 0.2)}`,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: SEARCE_COLORS.primary,
                      animation: 'blink 1.5s ease-in-out infinite',
                      '@keyframes blink': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.4 },
                      },
                    }}
                  />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: SEARCE_COLORS.primary,
                      fontWeight: 600,
                    }}
                  >
                    📊 Slide Created
                  </Typography>
                </Box>
              )}
              
              {/* OK Button */}
              <Button
                variant="contained"
                onClick={() => {
                  navigate('/customer-sentiment');
                }}
                sx={{
                  mt: 2,
                  px: 6,
                  py: 1.5,
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  backgroundColor: SEARCE_COLORS.primary,
                  boxShadow: `0 4px 14px ${alpha(SEARCE_COLORS.primary, 0.4)}`,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: SEARCE_COLORS.primaryDark || SEARCE_COLORS.secondary,
                    boxShadow: `0 6px 20px ${alpha(SEARCE_COLORS.primary, 0.5)}`,
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
              >
                Continue
              </Button>
            </Box>
          </Fade>
        )}
        
        {/* Error State */}
        {submissionError.stage !== null && (
          <Fade in={submissionError.stage !== null}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
              {/* Error Icon */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${alpha(SEARCE_COLORS.error, 0.15)} 0%, ${alpha(SEARCE_COLORS.error, 0.05)} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'errorShake 0.5s ease-out',
                  '@keyframes errorShake': {
                    '0%, 100%': { transform: 'translateX(0)' },
                    '20%, 60%': { transform: 'translateX(-8px)' },
                    '40%, 80%': { transform: 'translateX(8px)' },
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{ fontSize: 40 }}
                >
                  {submissionError.stage === 'upload' && '📁'}
                  {submissionError.stage === 'submission' && '⚠️'}
                  {submissionError.stage === 'email' && '📧'}
                </Box>
              </Box>
              
              {/* Error Stage Label */}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 0.5,
                  backgroundColor: alpha(SEARCE_COLORS.error, 0.1),
                  borderRadius: 2,
                }}
              >
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: SEARCE_COLORS.error,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {submissionError.stage === 'upload' && 'File Upload Failed'}
                  {submissionError.stage === 'submission' && 'Submission Failed'}
                  {submissionError.stage === 'email' && 'Email Delivery Failed'}
                </Typography>
              </Box>
              
              {/* Ticket ID - shown when request was created but email failed */}
              {submissionError.ticketId && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2.5,
                    py: 1,
                    backgroundColor: alpha(SEARCE_COLORS.primary, 0.08),
                    border: `1px solid ${alpha(SEARCE_COLORS.primary, 0.2)}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: SEARCE_COLORS.textSecondary,
                      fontWeight: 500,
                    }}
                  >
                    Ticket ID:
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: SEARCE_COLORS.primary,
                      fontWeight: 700,
                      fontFamily: 'monospace',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {submissionError.ticketId}
                  </Typography>
                </Box>
              )}
              
              {/* Error Title */}
              <Typography 
                variant="h5" 
                sx={{ 
                  color: SEARCE_COLORS.error,
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  textAlign: 'center',
                }}
              >
                {submissionError.message}
              </Typography>
              
              {/* Error Details */}
              {submissionError.details && (
                <Box
                  sx={{
                    backgroundColor: alpha(SEARCE_COLORS.error, 0.05),
                    border: `1px solid ${alpha(SEARCE_COLORS.error, 0.15)}`,
                    borderRadius: 2,
                    p: 2,
                    maxWidth: '100%',
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: SEARCE_COLORS.textSecondary,
                      textAlign: 'center',
                      lineHeight: 1.6,
                      wordBreak: 'break-word',
                    }}
                  >
                    {submissionError.details}
                  </Typography>
                </Box>
              )}
              
              {/* Action Buttons - Different for each error stage */}
              
              {/* Upload Error: Allow retry or modify file */}
              {submissionError.stage === 'upload' && (
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      onClearError();
                      onClearFile();
                    }}
                    sx={{
                      px: 4,
                      py: 1.25,
                      borderRadius: 3,
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: SEARCE_COLORS.border,
                      color: SEARCE_COLORS.textPrimary,
                      '&:hover': {
                        borderColor: SEARCE_COLORS.textSecondary,
                        backgroundColor: alpha(SEARCE_COLORS.neutral_100, 0.5),
                      },
                    }}
                  >
                    Change File
                  </Button>
                  <Button
                    variant="contained"
                    onClick={onRetryUpload}
                    sx={{
                      px: 4,
                      py: 1.25,
                      borderRadius: 3,
                      textTransform: 'none',
                      fontWeight: 600,
                      backgroundColor: SEARCE_COLORS.primary,
                      boxShadow: `0 4px 14px ${alpha(SEARCE_COLORS.primary, 0.4)}`,
                      '&:hover': {
                        backgroundColor: SEARCE_COLORS.primaryDark || SEARCE_COLORS.secondary,
                        boxShadow: `0 6px 20px ${alpha(SEARCE_COLORS.primary, 0.5)}`,
                      },
                    }}
                  >
                    Retry Upload
                  </Button>
                </Box>
              )}
              
              {/* Submission/Email Error: Show error and redirect to dashboard */}
              {(submissionError.stage === 'submission' || submissionError.stage === 'email') && (
                <Button
                  variant="contained"
                  onClick={() => {
                    onClearError();
                    navigate('/customer-sentiment');
                  }}
                  sx={{
                    px: 5,
                    py: 1.25,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontWeight: 600,
                    backgroundColor: SEARCE_COLORS.primary,
                    boxShadow: `0 4px 14px ${alpha(SEARCE_COLORS.primary, 0.4)}`,
                    '&:hover': {
                      backgroundColor: SEARCE_COLORS.primaryDark || SEARCE_COLORS.secondary,
                      boxShadow: `0 6px 20px ${alpha(SEARCE_COLORS.primary, 0.5)}`,
                    },
                  }}
                >
                  Go to Dashboard
                </Button>
              )}
            </Box>
          </Fade>
        )}
      </Box>
    </>
  );
};
